import {
  CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import type { LeadInteraction, Owner } from '../../../@types/Owner';
import { useAdsModal } from '../../../hooks/useAdsModal';
import AdsModal from '../../common/AdsModal/AdsModal';
import MUITable from '../../common/MUITable';
import { formatDpeDetailsFromOwner } from '../utils/formatters';
import CoproLocalOwned from './CoproLocalOwned';
import DpeDetailsPopup from './DpeDetailsPopup';
import OwnerInfos from './OwnerInfos';
import { LastContactInfo } from './PreviousContactList';

interface ManagerInteraction {
  meetingAt?: string | null;
  result?: string | null;
  amount?: string | null;
  createdAt: string;
}

const INTERACTION_CONFIG = {
  salesManagerInteractions: {
    clientClosedLabel: 'Client converti',
    professionalActionDoneLabel: 'Action effectuée - Sans suite',
    rejectedLabel: 'Échec',
    showLeadInteractionAmount: false,
    sortFields: { owner: 'owner.firstname', address: 'owner.address', dpe: 'dpe.value' },
    headerClass: 'w-1/5',
  },
  rentManagerInteractions: {
    clientClosedLabel: 'Contrat signé',
    professionalActionDoneLabel: 'Prestation réalisée - Sans suite',
    rejectedLabel: 'Refusé',
    showLeadInteractionAmount: true,
    sortFields: { owner: 'lastname', address: 'address', dpe: 'dpeClass' },
    headerClass: 'w-1/6',
  },
} as const;

type InteractionKey = keyof typeof INTERACTION_CONFIG;

interface ClosedFollowUpTableProps {
  interactionKey: InteractionKey;
  data: Owner[];
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  onSortChange?: (field: string) => void;
  currentSortField?: string;
}

const ClosedFollowUpTable: React.FC<ClosedFollowUpTableProps> = ({
  interactionKey,
  data,
  totalCount,
  onPageChange,
  currentPage,
  currentRowsPerPage,
  onSortChange,
  currentSortField = '',
}) => {
  const [isDpeDetailsOpen, setIsDpeDetailsOpen] = useState(false);
  const [selectedDpe, setSelectedDpe] = useState<Record<string, unknown> | null>(null);
  const { handleAdsClick, adsModalProps } = useAdsModal();
  const config = INTERACTION_CONFIG[interactionKey];

  const formatDate = (dateString: string) => {
    if (!dateString) {return 'N/A';}
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getOwnerFromItem = (item: Owner): Owner => item;

  const getLeadInteraction = (owner: Owner): LeadInteraction | undefined => {
    if (!owner.leadInteractions || !Array.isArray(owner.leadInteractions) || owner.leadInteractions.length === 0) {
      return undefined;
    }
    return [...owner.leadInteractions]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  };

  const getInteractionFromItem = (owner: Owner): Partial<ManagerInteraction> => {
    const interactions = owner[interactionKey] || [];
    if (interactions.length > 0) {
      const sortedInteractions = [...interactions].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      return sortedInteractions[0];
    }
    return {};
  };

  const getMeetingResultIcon = (result?: string, leadInteraction?: LeadInteraction) => {
    if (leadInteraction) {
      if (leadInteraction.clientClosed) {
        return <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />;
      }
      if (leadInteraction.professionalActionDone) {
        return <XCircleIcon className="h-5 w-5 shrink-0 text-red-600" />;
      }
    }
    if (!result) {return null;}
    if (result.toLowerCase().includes('signé') || result.toLowerCase().includes('succès') ||
        result.toLowerCase().includes('signed') || result.toLowerCase().includes('success')) {
      return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
    }
    if (result.toLowerCase().includes('annulé') || result.toLowerCase().includes('échec') ||
        result.toLowerCase().includes('rejected') || result.toLowerCase().includes('failure')) {
      return <XCircleIcon className="h-5 w-5 text-red-600" />;
    }
    return null;
  };

  const getMeetingResultLabel = (result?: string | null, leadInteraction?: LeadInteraction): string => {
    if (leadInteraction) {
      if (leadInteraction.clientClosed) {
        return config.clientClosedLabel;
      }
      if (leadInteraction.professionalActionDone) {
        return config.professionalActionDoneLabel;
      }
    }
    if (!result) {return 'Non renseigné';}
    if (result.toLowerCase().includes('signed') || result.toLowerCase().includes('success')) {
      return 'Signé';
    }
    if (result.toLowerCase().includes('rejected') || result.toLowerCase().includes('failure')) {
      return config.rejectedLabel;
    }
    return result;
  };

  const getResultDisplay = (item: Owner) => {
    const owner = getOwnerFromItem(item);
    const interaction = getInteractionFromItem(owner);
    const leadInteraction = owner ? getLeadInteraction(owner) : undefined;
    const amount = config.showLeadInteractionAmount
      ? (leadInteraction?.data?.amount ?? interaction.amount)
      : (!leadInteraction && interaction.amount) ? interaction.amount : null;

    return (
      <div>
        <div className="flex items-center">
          {getMeetingResultIcon(interaction.result, leadInteraction)}
          <span className="ml-2 font-medium">
            {getMeetingResultLabel(interaction.result, leadInteraction)}
          </span>
        </div>
        {leadInteraction && (
          <div className="mt-1 text-sm">
            <div className="text-gray-600">
              Le {formatDate(leadInteraction.interactionAt)}
            </div>
            {leadInteraction.interactionWith && (
              <div className="text-gray-600">
                {leadInteraction.interactionWith.firstname} {leadInteraction.interactionWith.lastname}
              </div>
            )}
          </div>
        )}
        {amount && (
          <div className="mt-1 text-sm text-gray-800">
            <span className="font-medium">Montant:</span> {amount}€
          </div>
        )}
      </div>
    );
  };

  const handleDpeDetailsClick = (dpeDetails: Record<string, unknown>) => {
    setSelectedDpe(dpeDetails);
    setIsDpeDetailsOpen(true);
  };

  const getDpeDetails = (owner?: Owner): React.ReactNode => {
    return formatDpeDetailsFromOwner(owner, handleDpeDetailsClick, handleAdsClick);
  };

  const tableData = data.map(item => {
    const owner = getOwnerFromItem(item);
    const interaction = getInteractionFromItem(owner);
    return {
      'Propriétaire': <OwnerInfos owner={owner} showContacts={true} />,
      'Propriété': <CoproLocalOwned owner={owner} />,
      'Dernier contact': owner ? <LastContactInfo owner={owner} /> : <div className="text-gray-500">Aucune information de contact</div>,
      'Date de RDV': interaction.meetingAt ? formatDate(interaction.meetingAt) : 'Non renseigné',
      'Résultat': getResultDisplay(item),
      'DPE': getDpeDetails(owner),
    };
  });

  const headerClass = config.headerClass;
  const headers = [
    { title: 'Propriétaire', class: headerClass, sortField: config.sortFields.owner, sortable: !!onSortChange, onSort: onSortChange },
    { title: 'Propriété', class: headerClass, sortField: config.sortFields.address, sortable: !!onSortChange, onSort: onSortChange },
    { title: 'Dernier contact', class: headerClass, sortField: 'contacts.createdAt', sortable: !!onSortChange, onSort: onSortChange },
    { title: 'Date de RDV', class: headerClass, sortField: 'meetingAt', sortable: !!onSortChange, onSort: onSortChange },
    { title: 'Résultat', class: headerClass, sortField: 'result', sortable: !!onSortChange, onSort: onSortChange },
    { title: 'DPE', class: headerClass, sortField: config.sortFields.dpe, sortable: !!onSortChange, onSort: onSortChange },
  ];

  return (
    <>
      <MUITable
        data={tableData}
        headers={headers}
        isSearchable={true}
        tableClass="w-full"
        disablePagination={false}
        totalCount={totalCount}
        onPageChange={onPageChange}
        currentPage={currentPage}
        currentRowsPerPage={currentRowsPerPage}
        apiPagination={!!onPageChange}
        currentSortField={currentSortField}
      />
      {selectedDpe && (
        <DpeDetailsPopup
          isOpen={isDpeDetailsOpen}
          onClose={() => setIsDpeDetailsOpen(false)}
          dpeDetails={selectedDpe}
        />
      )}
      <AdsModal {...adsModalProps} />
    </>
  );
};

export default ClosedFollowUpTable;
